import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PENDING_TITLE } from "@/lib/labels/inlineReference";
import { createPortal } from "react-dom";
import type { Editor } from "@tiptap/core";
import {
  INACTIVE,
  refPickerPluginKey,
  type RefPickerState,
} from "@/lib/tiptap/extensions/refPickerTrigger";
import { testIds } from "@/testIds";
import {
  buildRows,
  flatten,
  formFor,
  PENDING_SLOT,
  type LibraryEntry,
  type PickerFilter,
  type Row,
} from "./refPickerModel";
import { useDocumentReferences } from "./useDocumentReferences";

/**
 * The `@` picker.
 *
 * One trigger, two groups — *In this document* above *From your library*, always
 * — and it writes whichever command the chosen target needs. Nothing on it says
 * "citation" or "reference": two group headers and a cursor. The distinction is
 * ours; the author is pointing at something.
 * (Olivia, lilia-docs/design-handoffs/2026-09-21-next-two-tools/SPEC-references.md §2)
 *
 * Keys: ↑ ↓ move · ↵ insert · ⇥ library only · esc close and keep typing.
 */

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

const WIDTH = 340;
const ROW_HEIGHT = 34;

export function RefPicker({
  editor,
  documentId,
  fetchWithAuth,
  library,
}: {
  editor: Editor | null;
  documentId?: string;
  fetchWithAuth?: Fetch;
  library: LibraryEntry[];
}) {
  const [state, setState] = useState<RefPickerState>(INACTIVE);
  const [selected, setSelected] = useState(0);
  // ⇥ narrows to the library without the author retyping the trigger.
  const [libraryOnly, setLibraryOnly] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editor) return;
    const read = () => setState(refPickerPluginKey.getState(editor.state) ?? INACTIVE);
    editor.on("transaction", read);
    return () => {
      editor.off("transaction", read);
    };
  }, [editor]);

  // A new trigger starts unfiltered.
  useEffect(() => {
    if (!state.active) setLibraryOnly(false);
  }, [state.active]);

  const targets = useDocumentReferences(documentId, fetchWithAuth, state.active);

  const filter: PickerFilter = libraryOnly ? "library" : state.filter;
  const groups = useMemo(
    () => buildRows({ targets, library, query: state.query, filter }),
    [targets, library, state.query, filter],
  );
  const rows = useMemo(() => flatten(groups), [groups]);

  useEffect(() => setSelected(0), [state.query, filter, state.active]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${selected}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const insert = useCallback(
    (row: Row) => {
      if (!editor) return;
      const node =
        row.group === "document"
          ? { type: "reference", attrs: { label: row.key, form: formFor(row) } }
          : // "bare" is \cite — what @cite{ has always produced, and what the
            // spec asks a library entry to emit.
            { type: "citation", attrs: { citeKey: row.key, mode: "bare" } };
      editor
        .chain()
        .focus()
        .insertContentAt({ from: state.from, to: state.to }, [node, { type: "text", text: " " }])
        .run();
    },
    [editor, state.from, state.to],
  );

  const dismiss = useCallback(() => {
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta(refPickerPluginKey, "dismiss"));
  }, [editor]);

  // A layout effect: the handler matches the rows on screen from the moment they
  // show, so a fast ↵ (typing "@top⏎") never reaches the previous, empty list.
  useLayoutEffect(() => {
    if (!state.active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        dismiss();
        return;
      }
      if (e.key === "Tab") {
        // ⇥ is "library only", and it must not move focus out of the editor.
        e.preventDefault();
        e.stopPropagation();
        setLibraryOnly((v) => !v);
        return;
      }
      if (rows.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelected((i) => (i + 1) % rows.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelected((i) => (i - 1 + rows.length) % rows.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        insert(rows[selected]);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [state.active, rows, selected, insert, dismiss]);

  if (!editor || !state.active || rows.length === 0) return null;

  let coords: { left: number; top: number; bottom: number };
  try {
    coords = editor.view.coordsAtPos(state.to);
  } catch {
    return null;
  }
  const height = Math.min(rows.length * ROW_HEIGHT + 60, 320);
  const below = window.innerHeight - coords.bottom - 6 >= height;
  const top = below ? coords.bottom + 6 : Math.max(6, coords.top - height - 6);
  const left = Math.max(6, Math.min(coords.left, window.innerWidth - WIDTH - 8));

  let index = -1;
  const renderGroup = (label: string, groupRows: Row[], testId: string) =>
    groupRows.length === 0 ? null : (
      <div data-testid={testId}>
        <div className="px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[color:var(--lilia-text-secondary)]">
          {label}
        </div>
        {groupRows.map((row) => {
          index += 1;
          const i = index;
          const on = i === selected;
          return (
            <button
              key={`${row.group}:${row.key}`}
              type="button"
              data-testid={testIds.refPicker.row}
              data-ref-key={row.key}
              data-ref-group={row.group}
              data-index={i}
              aria-selected={on}
              role="option"
              onMouseDown={(e) => {
                // Keep the editor's selection: a mousedown that blurs it would
                // move the cursor before the insert lands.
                e.preventDefault();
                insert(row);
              }}
              onMouseEnter={() => setSelected(i)}
              className="flex w-full items-baseline gap-2 px-3 text-left"
              style={{
                height: ROW_HEIGHT,
                background: on ? "var(--lilia-hover)" : "transparent",
              }}
            >
              {row.group === "document" && (
                <span
                  className="w-[2.2ch] shrink-0 text-right font-mono text-[12px] tabular-nums"
                  style={{ color: row.number ? "var(--lilia-text)" : "var(--lilia-text-secondary)" }}
                  title={row.number ? undefined : PENDING_TITLE}
                >
                  {row.number ?? PENDING_SLOT}
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-[13px] text-[color:var(--lilia-text)]">
                {row.label}
              </span>
              <span className="shrink-0 truncate font-mono text-[11px] text-[color:var(--lilia-text-secondary)]">
                {row.key}
              </span>
            </button>
          );
        })}
      </div>
    );

  return createPortal(
    <div
      data-testid={testIds.refPicker.root}
      data-filter={filter}
      role="listbox"
      className="fixed overflow-hidden rounded-[var(--lilia-radius-nested,8px)] shadow-lg"
      style={{
        top,
        left,
        width: WIDTH,
        zIndex: 1000,
        background: "var(--lilia-surface)",
        border: "1px solid var(--lilia-border)",
      }}
    >
      <div ref={listRef} className="max-h-[280px] overflow-y-auto pb-1">
        {renderGroup("In this document", groups.document, testIds.refPicker.groupDocument)}
        {renderGroup("From your library", groups.library, testIds.refPicker.groupLibrary)}
      </div>
      <div
        className="flex gap-3 px-3 py-1.5 text-[11px] text-[color:var(--lilia-text-secondary)]"
        style={{ borderTop: "1px solid var(--lilia-border)" }}
      >
        <span>↵ insert</span>
        <span>{libraryOnly ? "⇥ both" : "⇥ library only"}</span>
        <span>esc keep typing</span>
      </div>
    </div>,
    document.body,
  );
}
